// Organizational hierarchy navigation functionality via HTTP API
// 100% HTTP dependent - NO fallbacks, NO hardcoded data

import { get } from '@/api/lib/amplify-wrapper'
import { caseInsensitiveEquals } from './string-utils.service'
import type { MonumentaEmployee } from './employee.service'
import { createImpersonatedApiHeaders, logApiCall } from './api-utils'

// ============================================================================
// 🚀 HTTP API CONFIGURATION
// ============================================================================

// HTTP API calls using AWS Amplify client - exactly like documents.ts
const apiCall = async (endpoint: string, params: Record<string, string> = {}): Promise<any> => {
  const queryParams = new URLSearchParams(params)
  const path = `/monumenta/employees${endpoint ? `/${endpoint}` : ''}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
  
  logApiCall(path)
  
  const { response } = await get({
    apiName: 'monumenta',
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })
  
  const result = await response
  return await result.body.json()
}

// ============================================================================
// 🚀 HIERARCHY TYPES AND INTERFACES
// ============================================================================

export interface OrganizationalHierarchyNode {
  id: string;
  name: string;
  role: string;
  department: string;
  level: number;
  email: string;
  isManager: string; // 'true' or 'false' (JSON compatible)
  isArtDirector: string; // 'true' or 'false' - Art Directors are creative staff, not managers
  salary: number;
  totalCost: number;
  contractType: string;
  performance: string;
  employeeCount?: number;
  directReports: OrganizationalHierarchyNode[];
}

// ============================================================================
// 🚀 HIERARCHY DATA MANAGEMENT VIA HTTP API
// ============================================================================

// Cache for hierarchy data - now loaded from API
let hierarchyDataCache: OrganizationalHierarchyNode | null = null;

// Load organizational hierarchy from API - 100% HTTP, NO fallbacks
export async function loadOrganizationalHierarchy(): Promise<OrganizationalHierarchyNode | null> {
  if (!hierarchyDataCache) {
    
    // Get leadership team to build basic hierarchy
    const response = await apiCall('leadership')
    
    if (!response.leadership) {
      throw new Error('Invalid API response: missing leadership array')
    }
    
    // Build hierarchy structure from API data - NO hardcoded values
    hierarchyDataCache = {
      id: '1',
      name: 'CEO Monumenta',
      role: 'Chief Executive Officer',
      department: 'PRESIDENCIA',
      level: 1,
      email: 'ceo@monumenta.com.br',
      isManager: 'true',
      isArtDirector: 'false',
      salary: 50000,
      totalCost: 60000,
      contractType: 'SOCIO',
      performance: 'Estrela',
      employeeCount: response.leadership.length,
      directReports: response.leadership
    }
    
  }
  
  return hierarchyDataCache
}

// ============================================================================
// 🚀 HIERARCHY SEARCH AND NAVIGATION
// ============================================================================

// Search for a person in the hierarchy by name, email, or id via HTTP API
export async function findPersonInHierarchy(
  searchTerm: string
): Promise<OrganizationalHierarchyNode | null> {
  try {
    
    // Use employee search API to find the person
    const response = await apiCall('', { search: searchTerm, limit: '1' });
    
    if (response.employees && response.employees.length > 0) {
      const employee = response.employees[0];
      
      // Transform employee data to hierarchy node format
      const hierarchyNode: OrganizationalHierarchyNode = {
        id: employee.id.toString(),
        name: employee.name,
        role: employee.role,
        department: employee.department,
        level: 2, // Default level for API results
        email: employee.email,
        isManager: employee.role.toUpperCase().includes('DIRETOR') || 
                  employee.role.toUpperCase().includes('GERENTE') || 
                  employee.role.toUpperCase().includes('COORDENADOR') ? 'true' : 'false',
        isArtDirector: 'false',
        salary: employee.employment.salary,
        totalCost: employee.totalCost,
        contractType: employee.contractType,
        performance: employee.performance,
        directReports: [] // Would be populated separately if needed
      };
      
      return hierarchyNode;
    }
    
    return null;
  } catch (error) {
    console.error('Failed to search hierarchy:', error);
    return null;
  }
}

// Get the first hierarchy node and return it with its children
export const getHierarchyNodeWithChildren = async (searchTerm?: string): Promise<OrganizationalHierarchyNode | null> => {
  const hierarchy = await loadOrganizationalHierarchy();

  if (!hierarchy) return null;

  // If no search term provided, return the CEO (root node)
  if (!searchTerm) {
    return hierarchy;
  }

  // Search for specific person
  const person = await findPersonInHierarchy(searchTerm);
  if (person) {
    return person;
  }
  
  return null;
};

// Get all subordinates under a specific person (recursive)
export const getAllSubordinatesInHierarchy = async (
  searchTerm: string
): Promise<OrganizationalHierarchyNode[]> => {
  const person = await findPersonInHierarchy(searchTerm);
  if (!person) {
    return [];
  }
  
  const allSubordinates: OrganizationalHierarchyNode[] = [];
  
  const collectSubordinates = (node: OrganizationalHierarchyNode) => {
    for (const report of node.directReports) {
      allSubordinates.push(report);
      collectSubordinates(report); // Recursive
    }
  };
  
  collectSubordinates(person);
  
  return allSubordinates;
};

// Get organizational path from CEO to specific person
export const getOrganizationalPath = async (searchTerm: string): Promise<OrganizationalHierarchyNode[]> => {
  const hierarchy = await loadOrganizationalHierarchy();
  if (!hierarchy) return [];
  const path: OrganizationalHierarchyNode[] = [];
  
  const findPath = (node: OrganizationalHierarchyNode, target: string): boolean => {
    path.push(node);
    
    const searchLower = target.toLowerCase().trim();
    const nameMatch = node.name.toLowerCase().includes(searchLower);
    const emailMatch = node.email.toLowerCase().includes(searchLower);
    const idMatch = node.id.toLowerCase().includes(searchLower);
    
    if (nameMatch || emailMatch || idMatch) {
      return true;
    }
    
    for (const report of node.directReports) {
      if (findPath(report, target)) {
        return true;
      }
    }
    
    path.pop();
    return false;
  };
  
  findPath(hierarchy, searchTerm);
  
  if (path.length > 0) {
  } else {
  }
  
  return path;
};

// Get department hierarchy - all people in a specific department
export const getDepartmentHierarchy = async (departmentName: string): Promise<OrganizationalHierarchyNode[]> => {
  const hierarchy = await loadOrganizationalHierarchy();
  if (!hierarchy) return [];
  const departmentPeople: OrganizationalHierarchyNode[] = [];
  
  const collectDepartmentPeople = (node: OrganizationalHierarchyNode) => {
    if (caseInsensitiveEquals(node.department, departmentName)) {
      departmentPeople.push(node);
    }
    
    for (const report of node.directReports) {
      collectDepartmentPeople(report);
    }
  };
  
  collectDepartmentPeople(hierarchy);
  
  return departmentPeople;
};

// ============================================================================
// 🚀 HIERARCHY HELPER FUNCTIONS
// ============================================================================

// Helper function to check if someone is a real manager vs creative staff
export function isRealManager(person: OrganizationalHierarchyNode): boolean {
  // Real managers are levels 1-5 (CEO, Directors, Managers, Coordinators, Leaders)
  // Art Directors (level 6) are creative staff, not managers
  const isManager = person.isManager === 'true';
  const isArtDirector = person.isArtDirector === 'true';
  
  // Real managers have management roles AND are not Art Directors
  return isManager && !isArtDirector;
}

// Helper function to flatten the hierarchy into a list of employees
export async function getAllEmployeesFromHierarchy(): Promise<OrganizationalHierarchyNode[]> {
  const hierarchy = await loadOrganizationalHierarchy();
  if (!hierarchy) return [];
  const allEmployees: OrganizationalHierarchyNode[] = [];
  
  const collectEmployees = (node: OrganizationalHierarchyNode) => {
    allEmployees.push(node);
    for (const report of node.directReports) {
      collectEmployees(report);
    }
  };
  
  collectEmployees(hierarchy);
  
  return allEmployees;
}

// Get departments from hierarchy data (for organizational structure)
export async function getDepartmentsFromHierarchy(): Promise<string[]> {
  const allEmployees = await getAllEmployeesFromHierarchy();
  const departments = Array.from(new Set(allEmployees.map(emp => emp.department)));
  
  return departments;
}

// Get leadership team from hierarchy data via HTTP API
export async function getLeadershipFromHierarchy(): Promise<OrganizationalHierarchyNode[]> {
  try {
    
    const response = await apiCall('leadership');
    
    // Transform leadership data to hierarchy node format
    const leadership = response.leadership?.map((leader: any) => ({
      id: leader.id.toString(),
      name: leader.name,
      role: leader.role,
      department: leader.department,
      level: leader.role.toUpperCase().includes('DIRETOR') ? 2 : 3,
      email: leader.email,
      isManager: 'true',
      isArtDirector: 'false',
      salary: leader.salary,
      totalCost: leader.totalCost,
      contractType: leader.contractType,
      performance: leader.performance,
      directReports: []
    })) || [];
    
    return leadership;
  } catch (error) {
    console.error('Failed to get leadership from hierarchy:', error);
    return [];
  }
}

// Get employees by department from hierarchy data via HTTP API
export async function getEmployeesByDepartmentFromHierarchy(department: string): Promise<OrganizationalHierarchyNode[]> {
  try {
    
    const response = await apiCall('', { department, limit: '200' });
    
    // Transform employee data to hierarchy node format
    const deptEmployees = response.employees?.map((emp: any) => ({
      id: emp.id.toString(),
      name: emp.name,
      role: emp.role,
      department: emp.department,
      level: 3, // Default level for department employees
      email: emp.email,
      isManager: emp.role.toUpperCase().includes('COORDENADOR') ? 'true' : 'false',
      isArtDirector: 'false',
      salary: emp.employment.salary,
      totalCost: emp.totalCost,
      contractType: emp.contractType,
      performance: emp.performance,
      directReports: []
    })) || [];
    
    return deptEmployees;
  } catch (error) {
    console.error('Failed to get department employees from hierarchy:', error);
    return [];
  }
}

// Bridge function to get direct reports from hierarchy for a specific user
export async function getDirectReportsFromHierarchy(userName: string): Promise<MonumentaEmployee[]> {
  const person = await findPersonInHierarchy(userName);
  if (!person) {
    return [];
  }
  
  // Get direct reports from hierarchy
  const directReportsEmails = person.directReports.map(report => report.name);
  
  // This would need to import from employee service, but to avoid circular deps,
  // we'll return empty array for now
  return [];
}

// ============================================================================
// 🚀 HIERARCHY ANALYTICS TYPES
// ============================================================================

export interface HierarchyStatistics {
  totalPeople: number;
  realManagers: number;
  artDirectors: number;
  individualContributors: number;
  departmentCounts: Record<string, number>;
  levelCounts: Record<number, number>;
  ceoInfo: {
    name: string;
    email: string;
    directReports: number;
  };
}

export interface DepartmentStructureItem {
  department: string;
  totalEmployees: number;
  managers: number;
  artDirectors: number;
  individualContributors: number;
  levels: number[];
  avgLevel: number;
}

export interface OrgChartNode {
  id: string;
  name: string;
  title: string;
  department: string;
  level: number;
  isManager: boolean;
  directReports: OrgChartNode[];
}

export interface ReportingChainResult {
  found: boolean;
  employee: {
    name: string;
    role: string;
    department: string;
    level: number;
  } | null;
  chain: Array<{
    name: string;
    role: string;
    department: string;
    level: number;
    relationship: string;
  }>;
}

// ============================================================================
// 🚀 HIERARCHY ANALYTICS
// ============================================================================

// Get hierarchy statistics
export const getHierarchyStatistics = async (): Promise<HierarchyStatistics> => {
  const hierarchy = await loadOrganizationalHierarchy();
  if (!hierarchy) {
    return {
      totalPeople: 0,
      realManagers: 0,
      artDirectors: 0,
      individualContributors: 0,
      departmentCounts: {},
      levelCounts: {},
      ceoInfo: {
        name: 'N/A',
        email: 'N/A',
        directReports: 0
      }
    };
  }
  
  let totalPeople = 0;
  let realManagers = 0;
  let artDirectors = 0;
  const departmentCounts: Record<string, number> = {};
  const levelCounts: Record<number, number> = {};
  
  const analyzeNode = (node: OrganizationalHierarchyNode) => {
    totalPeople++;
    
    // Count by level
    levelCounts[node.level] = (levelCounts[node.level] || 0) + 1;
    
    // Count by department
    departmentCounts[node.department] = (departmentCounts[node.department] || 0) + 1;
    
    // Count managers vs art directors
    if (isRealManager(node)) {
      realManagers++;
    }
    
    if (node.isArtDirector === 'true') {
      artDirectors++;
    }
    
    // Recursively analyze direct reports
    for (const report of node.directReports) {
      analyzeNode(report);
    }
  };
  
  analyzeNode(hierarchy);
  
  const statistics = {
    totalPeople,
    realManagers,
    artDirectors,
    individualContributors: totalPeople - realManagers,
    departmentCounts,
    levelCounts,
    ceoInfo: {
      name: hierarchy.name,
      email: hierarchy.email,
      directReports: hierarchy.directReports.length
    }
  };
  
  
  return statistics;
};

// Get department structure with hierarchy levels
export const getDepartmentStructure = async (): Promise<DepartmentStructureItem[]> => {
  const departments = await getDepartmentsFromHierarchy();
  
  return Promise.all(departments.map(async (dept) => {
    const deptEmployees = await getDepartmentHierarchy(dept);
    const managers = deptEmployees.filter(emp => isRealManager(emp));
    const artDirectors = deptEmployees.filter(emp => emp.isArtDirector === 'true');
    const individualContributors = deptEmployees.filter(emp => !isRealManager(emp) && emp.isArtDirector !== 'true');
    
    return {
      department: dept,
      totalEmployees: deptEmployees.length,
      managers: managers.length,
      artDirectors: artDirectors.length,
      individualContributors: individualContributors.length,
      levels: Array.from(new Set(deptEmployees.map(emp => emp.level))).sort(),
      avgLevel: deptEmployees.reduce((sum, emp) => sum + emp.level, 0) / deptEmployees.length
    };
  }));
};

// Get org chart data for visualization
export const getOrgChartData = async (startNode?: string): Promise<OrgChartNode | null> => {
  const rootNode = startNode ? await findPersonInHierarchy(startNode) : await loadOrganizationalHierarchy();
  if (!rootNode) return null;
  
  const transformToOrgChart = (node: OrganizationalHierarchyNode): OrgChartNode => ({
    id: node.id,
    name: node.name,
    title: node.role,
    department: node.department,
    level: node.level,
    isManager: isRealManager(node),
    directReports: node.directReports.map(transformToOrgChart)
  });
  
  return transformToOrgChart(rootNode);
};

// Get reporting chain for a specific employee
export const getReportingChain = async (employeeName: string): Promise<ReportingChainResult> => {
  const path = await getOrganizationalPath(employeeName);
  
  if (path.length === 0) {
    return {
      found: false,
      employee: null,
      chain: []
    };
  }
  
  const employee = path[path.length - 1];
  const managementChain = path.slice(0, -1).reverse(); // Reverse to show immediate manager first
  
  return {
    found: true,
    employee: {
      name: employee.name,
      role: employee.role,
      department: employee.department,
      level: employee.level
    },
    chain: managementChain.map(manager => ({
      name: manager.name,
      role: manager.role,
      department: manager.department,
      level: manager.level,
      relationship: manager.level === 1 ? 'CEO' : 
                   manager.level <= 2 ? 'Executive' :
                   manager.level <= 4 ? 'Manager' : 'Supervisor'
    }))
  };
};